import { Button } from '@/components/atoms/NewPcButton';
import useCheckMembership from '@/hooks/useCheckMembership';
import { useState } from 'react';
import { useOrderDetails } from '../../OrderDetailsProvider';

import { CPLAT_API_ROUTES, orderQueries, usePost } from '@/api';
import { useAppSelector } from '@/redux/store';

import { useLog } from '@/hooks/V3/useLog';
import { useToast } from '@/hooks/V3/useToast';
import { deviceUuidState } from '@/store/atom';
import { CarrierState } from '@/types/shipping';
import { deepEqual } from '@/utils/deep-equal';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

const Controller = () => {
  const queryClient = useQueryClient();

  const [isMembershipModal, setIsMembershipModal] = useState(false);

  const { showToast } = useToast();
  const {
    checkedOrders,
    carrierState,
    setCheckedOrders,
    errorCarrierState,
    setErrorCarrierState,
    setCarrierState,
  } = useOrderDetails();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);

  // v1과 동일한 setInvoice API 사용
  const { mutateAsync: setInvoice } = usePost(
    CPLAT_API_ROUTES.setInvoice,
    undefined,
    undefined,
    true,
  );

  const isMember = useCheckMembership();
  const handleNavigateToMembership = () => {
    navigate('/more/membership');
  };

  const { setUserEventHistory } = useLog(user.UserNo);
  const deviceUuid = useRecoilValue(deviceUuidState);

  const handleBulkSetInvoice = async () => {
    if (!isMember) {
      setIsMembershipModal(true);
      return;
    }
    if (checkedOrders.length === 0) return;

    setUserEventHistory({
      eventName: 'bulk_set_invoice_execute_web_dashboard',
      eventValue: JSON.stringify({
        orderCount: checkedOrders.length,
        orderNos: checkedOrders.map((o) => o.orderNo),
      }),
      deviceUuid,
    });

    const hasErrorOrder = checkedOrders.some((order) =>
      errorCarrierState.some((errItem) => deepEqual(errItem.target, order)),
    );
    if (hasErrorOrder) {
      showToast('warning', '발송 처리 전에 운송장번호를 한번 더 확인해주세요');
      return;
    }

    const targetList = checkedOrders
      .map((order) => carrierState.find((item) => deepEqual(item.target, order)))
      .filter((item): item is CarrierState => Boolean(item));

    const requests = targetList.map((item) =>
      setInvoice({
        cplatToken: user.CplatToken,
        marketId: item.target.marketId,
        storeId: item.target.storeId,
        orderNo: item.target.orderNo,
        orderItemList: [item.target.orderItemNo],
        coupangShipmentBoxNo: item.target.coupangShipmentBoxNo,
        invoiceNo: item.trackingNumber,
        shippingCompanyCode: item.carrier?.shippingCompanyCode,
        TmallDeliveryNo: item.target.tmallDeliveryNo,
        ProductNo: item.target.productNo,
        Quantity: item.target.orderCount,
      })
        .then((res) => ({ res, item }))
        .catch((error) => ({ res: null, item, error })),
    );

    const results = await Promise.all(requests);

    // 성공 / 실패 분리
    const successItems = results.filter(({ res }) => res?.data?.code === '200');

    const failedItems = results.filter(({ res }) => res?.data?.code !== '200');

    console.log('successItems', successItems);
    console.log('failedItems', failedItems);

    // 성공 처리
    if (successItems.length > 0) {
      showToast('success', `${successItems.length}건 발송 처리 완료`);
    }

    // 실패 처리
    if (failedItems.length > 0) {
      setErrorCarrierState((prev) => {
        const newItems = failedItems
          .map(({ item }) => item)
          .filter(
            (failItem) => !prev.some((prevItem) => deepEqual(prevItem.target, failItem.target)),
          );
        return [...prev, ...newItems];
      });

      showToast(
        'warning',
        `${failedItems.length}건의 발송 정보에 오류가 있어요. 운송장 번호를 확인해주세요.`,
      );
    }

    // 체크 해제 및 상태 초기화는 공통
    setCheckedOrders([]);
    setCarrierState([]);

    // 성공이 1개라도 있으면 refetch
    if (successItems.length > 0) {
      queryClient.invalidateQueries({ queryKey: orderQueries.all() });
    }
  };

  return (
    <div className="sticky right-0 flex h-full items-center gap-[10px] bg-white/10 pl-[20px] backdrop-blur-[5px]">
      {isMember ? (
        <Button
          size={'small'}
          variant="solid"
          className="w-fit gap-1"
          onClick={handleBulkSetInvoice}
          disabled={checkedOrders.length === 0}
        >
          발송 처리
          {checkedOrders.length > 0 && <span className="Body5M14">{checkedOrders.length}</span>}
        </Button>
      ) : (
        <Button
          size="small"
          variant="solid"
          onClick={handleNavigateToMembership}
          className="h-[38px] px-[18px]"
        >
          멤버십 가입하고 상품 발송하기
        </Button>
      )}
    </div>
  );
};

export default Controller;
